import { Component, OnInit } from '@angular/core';
import { FiltroClientesComponent } from 'app/components/filtro-clientes/filtro-clientes.component';
import { PermissoesService } from 'app/services/permissoes/permissoes.service';
import { CommonModule } from '@angular/common';
import { DialogService } from 'app/services/dialog/dialog.service';
import { ClienteService } from 'app/services/cliente/cliente.service';
import { finalize } from 'rxjs';
import { Meta, Metas, topOperadores } from 'app/interfaces/Meta';
import { RouterLink } from '@angular/router';
import { Permissoes } from 'app/interfaces/permissoes';
import { MetaService } from 'app/services/meta/meta.service';
import { MediaGraficoComponent } from './media-grafico/media-grafico.component';
import { BarraProgressoComponent } from "./barra-progresso/barra-progresso.component";
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DialogTopOperadoresComponent } from './dialog-top-operadores/dialog-top-operadores.component';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { toPng } from 'html-to-image';
import { jsPDF }from 'jspdf';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-exibir-metas',
  standalone: true,
  providers: [
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: {} }
  ],
  imports: [
    FiltroClientesComponent,
    CommonModule,
    RouterLink,
    MediaGraficoComponent,
    BarraProgressoComponent,
    MatTableModule,
    MatDialogModule,
    DialogTopOperadoresComponent,
    MatTooltipModule,
    TranslocoModule
  ],
  templateUrl: './exibir-metas.component.html',
  styleUrl: './exibir-metas.component.css'
})
export class ExibirMetasComponent implements OnInit {

  public metas: Metas = [];
  public meta!: Meta | null;
  public loading: boolean = true;
  public placeholderMetas: string = '';
  public ultimaMetaId: number = this.recuperaUltimaMeta();
  public ultimaMetaVisualizada!: Metas[0];
  public jsPdfService = new jsPDF('p', 'mm', 'a4');
  public permissoes: Permissoes.Acessos = {
    criar: false,
    excluir: false,
  }
  public dataSource: MatTableDataSource<topOperadores> = new MatTableDataSource();
  public dataSourceLimited: MatTableDataSource<topOperadores> = new MatTableDataSource();
  public linguagemPreferida!: string;

  public readonly coresBarra = ['#C4E7FE', '#E5CCFF', '#CEF3D4'];
  public readonly coresReal = ['#0095FF', '#BF83FF', '#3CD856'];
  public readonly coresIdeal = ['#80CAFF', '#D8B2FF', '#89FF9C'];

  public readonly PERMISSAO_CRIAR = 'criar_metas_premoplan';
  public readonly PERMISSAO_EXCLUIR = 'excluir_metas_premoplan';

  constructor(
    private dialogService: DialogService,
    private clienteService: ClienteService,
    private metaService: MetaService,
    private permissoesService: PermissoesService,
    private dialog: MatDialog,
    private translocoService: TranslocoService
  ) { }

  ngOnInit(): void {
    this.linguagemPreferida = localStorage.getItem('linguagemPreferida') ?? 'pt';
    this.verificarPermissoes();
    this.recuperaUltimaMeta();
    this.placeholderMetas = 'COMPONENTS.METAS.loadingGoals';
  }

  public verificarPermissoes(): void {
    this.permissoes.criar = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_CRIAR);
    this.permissoes.excluir = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_EXCLUIR);
  }

  public recuperaUltimaMeta(): number {
    let ultimaMeta: string | null = localStorage.getItem('ultimaMetaVisualizada');
    if (ultimaMeta === null) {
      return 0;
    }
    return parseInt(ultimaMeta);
  }

  public buscarMetas(clienteId: number): void {
    this.placeholderMetas = 'COMPONENTS.METAS.loadingGoals';
    this.metas = [];
    this.meta = null;
    this.clienteService.buscarTodasMetas(clienteId)
    .pipe(finalize(() => this.loading = false))
    .subscribe({
      next: (metas: Metas) => {
        if (!metas || metas.length === 0) {
          this.placeholderMetas = 'COMPONENTS.METAS.noGoals';
            return;
        }

        this.metas = metas;
        this.placeholderMetas = 'COMPONENTS.METAS.selectGoal';
        this.carregarUltimaMeta();
        this.obterMeta();
      },
      error: () => {
        this.dialogService.erro(
          this.translocoService.translate('COMPONENTS.METAS.dialogs.fetchErrorTitle'),
          this.translocoService.translate('COMPONENTS.METAS.dialogs.fetchErrorMessage')
        );
      }
      });
  }

  public carregarUltimaMeta(): void {
    this.ultimaMetaVisualizada = this.metas.find(meta => meta.sr_id === this.ultimaMetaId) ?? this.metas[0];
  }

  public obterMeta(): void {
    this.dialogService.loading(this.translocoService.translate('COMPONENTS.METAS.dialogs.loadingGoal'));
    this.meta = null;
    this.loading = true;
    this.metaService.obterMeta(this.ultimaMetaVisualizada.sr_id!).subscribe({
      next: (meta: Meta) => {
        this.meta = meta;
        this.dialogService.fechar();
        this.loading = false;
        this.dataSource.data = this.meta.top_operadores;
        this.dataSourceLimited.data = this.dataSource.data.slice(0, 4);
        localStorage.setItem('ultimaMetaVisualizada', this.ultimaMetaVisualizada.sr_id!.toString());
      }, error: () => {
        this.loading = false;
        this.dialogService.erro(
          this.translocoService.translate('COMPONENTS.METAS.dialogs.fetchGoalErrorTitle'),
          this.translocoService.translate('COMPONENTS.METAS.dialogs.fetchGoalErrorMessage')
        );
      }
    });
  }

  public onFiltrar(idMeta: string | null): void {
    const idMetaSelecionada = idMeta ? Number(idMeta) : null;

    if (!idMetaSelecionada) {
      this.loading = false;
      return;
    }

    this.ultimaMetaVisualizada.sr_id = idMetaSelecionada;
    this.dialogService.loading(this.translocoService.translate('COMPONENTS.METAS.dialogs.loadingGoal'));

    this.obterMeta();
  }

  public async excluir(idMeta: number): Promise<void> {
    let resultado: boolean = await this.dialogService.confirmacao(
      this.translocoService.translate('COMPONENTS.METAS.dialogs.deleteTitle'),
      this.translocoService.translate('COMPONENTS.METAS.dialogs.deleteConfirm')
    );

    if (resultado) {
      this.dialogService.loading(this.translocoService.translate('COMPONENTS.METAS.dialogs.deletingGoal'));
      this.metaService.deletar(idMeta).subscribe({
        next: async () => {
          await this.dialogService.sucesso(
            this.translocoService.translate('COMPONENTS.METAS.dialogs.deleteSuccessTitle'),
            this.translocoService.translate('COMPONENTS.METAS.dialogs.deleteSuccessMessage')
          );
          this.metas = this.metas.filter(meta => meta.sr_id !== idMeta);
          this.meta = null;
          this.loading = false;
          if (this.metas.length === 0) {
            this.placeholderMetas = 'COMPONENTS.METAS.noGoals';
            return;
          }

          this.carregarUltimaMeta();
          this.obterMeta();
        },
        error: () => {
          this.dialogService.erro(
            this.translocoService.translate('COMPONENTS.METAS.dialogs.deleteErrorTitle'),
            this.translocoService.translate('COMPONENTS.METAS.dialogs.deleteErrorMessage')
          );
        }
      });
    }
  }

  public retornaCorTurno(tipo: 'barra' | 'real' | 'ideal', index: number): string {
    const cores = {
      barra: this.coresBarra,
      real: this.coresReal,
      ideal: this.coresIdeal
    }[tipo];
    return cores[index % cores.length];
  }

  public abrirDialogTopOperadores(): void {
    this.dialog.open(DialogTopOperadoresComponent, {
      width: '1000px',
      maxWidth: '1000px',
      data: this.dataSource.data
    });
  }

  public obterElementosPDF(): HTMLElement[] {
    return [
      document.getElementById('titulo-pagina') as HTMLElement,
      document.getElementById('previsao-produtividade') as HTMLElement,
      document.getElementById('graficos-meta') as HTMLElement,
      document.getElementById('container-turnos') as HTMLElement,
      document.getElementById('dialog-top-operadores') as HTMLElement
    ];
  }

  public async gerarImagemPDF(element: HTMLElement, positionY: number, larguraTela: number): Promise<number> {
    let pdfWidth = screen.width > 1560 ? 160 : 130;
    const base64Image = await this.geraPng(element);
    let imageHeight = ((element.offsetHeight - 10) * pdfWidth) / element.offsetWidth;
    const margin = larguraTela > 1560 ? 25 : 40;
    if (element.id === 'titulo-pagina') {
      imageHeight = ((element.offsetHeight - 20) * pdfWidth) / element.offsetWidth;
      pdfWidth /= 2;
    }

    this.jsPdfService.addImage(base64Image, 'PNG', margin, positionY, pdfWidth, imageHeight);

    return element.id === 'titulo-pagina' ? positionY + 15 : positionY + imageHeight;
  } 

  public async gerarRelatorio(elementos: HTMLElement[], larguraTela: number) {
    let posicaoY = 5;

    for (const elemento of elementos) {
      if (elemento) {
        posicaoY = await this.gerarImagemPDF(elemento, posicaoY, larguraTela);
      }
    }
    this.jsPdfService.save('Relatorio-' + this.meta?.vc_nome + '.pdf');
    this.dialogService.fechar();
  };

  public async exportarPDF(): Promise<void> {
    this.dialogService.loading(this.translocoService.translate('COMPONENTS.METAS.dialogs.generatingPdf'));

    const larguraTela = document.documentElement.clientWidth;
    
    const elementos = this.obterElementosPDF();

    await this.gerarRelatorio(elementos, larguraTela);
  }

  public async geraPng(element: HTMLElement): Promise<string> {
    const actions = element.querySelector<HTMLElement>('#acoes');

    if (actions) {
      actions.classList.toggle('d-none');
    }

    const base64Image = await toPng(element);

    if (actions) {
      actions.classList.toggle('d-none');
    }

    return base64Image;
  }
}
